import {FC, useState} from 'react'
import {Link} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {KTIcon, toAbsoluteUrl} from '../../../_metronic/helpers'
import {Helmet} from 'react-helmet-async'
import {createContactRequest} from './_requests'

const ContactUs: FC = () => {
  const intl = useIntl()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    subject: '',
    message: '',
  })

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(null)
    try {
      await createContactRequest(formData)
      setSubmitSuccess(intl.formatMessage({id: 'CONTACT.FORM.SUCCESS'}))
      setFormData({name: '', email: '', mobile: '', subject: '', message: ''})
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message || intl.formatMessage({id: 'CONTACT.FORM.ERROR'})
      setSubmitError(apiMsg)
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage({id: 'CONTACT.META.TITLE'})}</title>
        <meta name='description' content={intl.formatMessage({id: 'CONTACT.META.DESC'})} />
        <meta property='og:title' content={intl.formatMessage({id: 'CONTACT.META.TITLE'})} />
        <meta property='og:description' content={intl.formatMessage({id: 'CONTACT.META.DESC'})} />
      </Helmet>

      {/* Hero Section */}
      <section className='hero-section contact-hero'>
        <div className='hero-content'>
          <div className='mb-4'>
            <span className='badge px-4 py-2 rounded-pill'>
              <KTIcon iconName='message-text-2' className='fs-2 text-warning ms-2' />
              {intl.formatMessage({id: 'CONTACT.HERO.BADGE'})}
            </span>
          </div>
          <h1 className='hero-title'>
            {intl.formatMessage({id: 'CONTACT.HERO.TITLE'})}
          </h1>
          <p className='hero-desc'>
            {intl.formatMessage({id: 'CONTACT.HERO.DESC'})}
          </p>
        </div>
        <div className='hero-image'>
          <img
            src={toAbsoluteUrl('/media/illustrations/energy/solar-nft-cta.webp')}
            alt='تماس با پشتیبانی'
            style={{maxWidth: 570}}
          />
        </div>
        <button
          className='hero-scroll-btn'
          onClick={() => {
            const nextSection = document.getElementById('first-content-section')
            if (nextSection) nextSection.scrollIntoView({behavior: 'smooth'})
          }}
          aria-label={intl.formatMessage({id: 'COMMON.SCROLL_DOWN'})}
        >
          <svg
            className='hero-scroll-svg'
            width='120'
            height='64'
            viewBox='0 0 120 64'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M88.0935 28C88.0935 12.536 75.3811 0 59.6996 0C44.0181 0 31.3057 12.536 31.3057 28C31.3057 43.464 44.0181 56 59.6996 56C75.3811 56 88.0935 43.464 88.0935 28Z'
              fill='#F1F1F3'
            ></path>
            <path
              d='M39.5969 8.86421C32.3121 15.6129 24.4015 27.5594 4.6961 27.8366C-15.0093 28.1137 33.673 39.7129 33.673 39.7129C33.673 39.7129 46.8818 2.11406 39.5969 8.86421Z'
              fill='#F1F1F3'
            ></path>
            <path
              d='M80.4024 8.86412C86.625 16.1764 95.5975 27.5595 115.304 27.8366C135.01 28.1138 86.3273 39.7129 86.3273 39.7129C86.3273 39.7129 74.1798 1.55253 80.4024 8.86412Z'
              fill='#F1F1F3'
            ></path>
            <path
              d='M54.6667 22.3334L60 27.6667L65.3334 22.3334'
              stroke='#19191A'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            ></path>
          </svg>
        </button>
      </section>

      {/* اطلاعات تماس */}
      <div id='first-content-section' className='solar-panel-section'>
        <div className='container'>
          <div className='solar-panel-card solar-panel-card-contact'>
            <div className='row g-5 align-items-center'>
              <div className='col-md-6 col-lg-6 col-xl-6 col-xxl-6 col-12'>
                <div className='solar-panel-content'>
                  <div className='contact-info-list'>
                    <div className='contact-info-item'>
                      <div className='contact-icon'>
                        <KTIcon iconName='geolocation' className='text-primary' />
                      </div>
                      <div className='contact-details'>
                        <h4>{intl.formatMessage({id: 'CONTACT.ADDRESS_TITLE'})}</h4>
                        <p>{intl.formatMessage({id: 'CONTACT.ADDRESS_TEXT'})}</p>
                        <span className='contact-meta'>{intl.formatMessage({id: 'CONTACT.ADDRESS_POSTAL'})}</span>
                      </div>
                    </div>

                    <div className='contact-info-item'>
                      <div className='contact-icon'>
                        <KTIcon iconName='phone' className='text-success' />
                      </div>
                      <div className='contact-details'>
                        <h4>{intl.formatMessage({id: 'CONTACT.PHONE_TITLE'})}</h4>
                        <p>
                          <a href='tel:02333330970' className='contact-link'>02333330970</a>
                          {' - '}
                          <a href='tel:02333330790' className='contact-link'>02333335754</a>
                        </p>
                        <span className='contact-meta'>{intl.formatMessage({id: 'CONTACT.PHONE_HOURS'})}</span>
                      </div>
                    </div>

                    <div className='contact-info-item'>
                      <div className='contact-icon'>
                        <KTIcon iconName='message-text-2' className='text-info' />
                      </div>
                      <div className='contact-details'>
                        <h4>{intl.formatMessage({id: 'CONTACT.EMAIL_TITLE'})}</h4>
                        <p>
                          <a href='mailto:info@bitsobit.academy' className='contact-link'>info@bitsobit.academy</a>
                        </p>
                        <span className='contact-meta'>{intl.formatMessage({id: 'CONTACT.EMAIL_RESPONSE'})}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-lg-6 col-12'>
                <div className='contact-map-wrapper'>
                  <iframe
                    src='https://maps.google.com/maps?q=35.575223783962855,53.38117020686075&z=15&output=embed'
                    width='100%'
                    height='400'
                    style={{border: 0, borderRadius: 16}}
                    allowFullScreen
                    loading='lazy'
                    title='موقعیت دفتر زنجیره بلوک انرژی'
                    className='contact-map'
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* فرم تماس */}
      <section className='contact-form-section py-20'>
        <div className='container'>
          <div className='text-center mb-8'>
            <h2 className='section-title mb-3'>{intl.formatMessage({id: 'CONTACT.FORM.TITLE'})}</h2>
            <p className='text-gray-600 fs-5 section-desc'>
              {intl.formatMessage({id: 'CONTACT.FORM.DESC'})}
            </p>
          </div>

          <div className='row justify-content-center'>
            <div className='col-lg-8 col-xl-7'>
              <div className='contact-form-card'>
                <form onSubmit={handleSubmit} className='contact-form'>
                  <div className='row g-4'>
                    <div className='col-md-6'>
                      <div className='form-group'>
                        <label className='form-label'>{intl.formatMessage({id: 'CONTACT.FORM.NAME'})}</label>
                        <input
                          type='text'
                          name='name'
                          className='form-control form-control-lg'
                          placeholder={intl.formatMessage({id: 'CONTACT.FORM.NAME_PLACEHOLDER'})}
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className='col-md-6'>
                      <div className='form-group'>
                        <label className='form-label'>{intl.formatMessage({id: 'CONTACT.FORM.EMAIL'})}</label>
                        <input
                          type='email'
                          name='email'
                          className='form-control form-control-lg'
                          placeholder={intl.formatMessage({id: 'CONTACT.FORM.EMAIL_PLACEHOLDER'})}
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className='col-md-12'>
                      <div className='form-group'>
                        <label className='form-label'>{intl.formatMessage({id: 'CONTACT.FORM.MOBILE'})}</label>
                        <input
                          type='tel'
                          name='mobile'
                          className='form-control form-control-lg text-end'
                          placeholder={intl.formatMessage({id: 'CONTACT.FORM.MOBILE_PLACEHOLDER'})}
                          value={formData.mobile}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className='col-12'>
                      <div className='form-group'>
                        <label className='form-label'>{intl.formatMessage({id: 'CONTACT.FORM.SUBJECT'})}</label>
                        <select
                          name='subject'
                          className='form-select form-select-lg'
                          value={formData.subject}
                          onChange={handleChange}
                          required
                        >
                          <option value=''>{intl.formatMessage({id: 'CONTACT.FORM.SUBJECT_PLACEHOLDER'})}</option>
                          <option value='signals'>{intl.formatMessage({id: 'CONTACT.FORM.SUBJECT_SIGNALS'})}</option>
                          <option value='technical'>{intl.formatMessage({id: 'CONTACT.FORM.SUBJECT_TECHNICAL'})}</option>
                          <option value='billing'>{intl.formatMessage({id: 'CONTACT.FORM.SUBJECT_BILLING'})}</option>
                          <option value='support'>{intl.formatMessage({id: 'CONTACT.FORM.SUBJECT_SUPPORT'})}</option>
                          <option value='other'>{intl.formatMessage({id: 'CONTACT.FORM.SUBJECT_OTHER'})}</option>
                        </select>
                      </div>
                    </div>

                    <div className='col-12'>
                      <div className='form-group'>
                        <label className='form-label'>{intl.formatMessage({id: 'CONTACT.FORM.MESSAGE'})}</label>
                        <textarea
                          name='message'
                          className='form-control form-control-lg'
                          rows={5}
                          placeholder={intl.formatMessage({id: 'CONTACT.FORM.MESSAGE_PLACEHOLDER'})}
                          value={formData.message}
                          onChange={handleChange}
                          required
                        ></textarea>
                      </div>
                    </div>

                    <div className='col-12'>
                      <div className='form-submit'>
                        <button
                          type='submit'
                          className='btn btn-primary btn-lg px-8'
                          disabled={submitting}
                        >
                          <KTIcon iconName='send' className='fs-2 ms-2' />
                          {submitting ? intl.formatMessage({id: 'CONTACT.FORM.SENDING'}) : intl.formatMessage({id: 'CONTACT.FORM.SUBMIT'})}
                        </button>
                        {submitSuccess && <div className='text-success mt-4'>{submitSuccess}</div>}
                        {submitError && <div className='text-danger mt-2'>{submitError}</div>}
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* شبکه‌های اجتماعی */}
      <section className='social-section py-20'>
        <div className='container'>
          <div className='social-card'>
            <div className='row g-5 align-items-center'>
              <div className='col-lg-6 col-12'>
                <div className='social-content'>
                  <h2 className='social-title'>{intl.formatMessage({id: 'CONTACT.SOCIAL.TITLE'})}</h2>
                  <p className='social-description'>
                    {intl.formatMessage({id: 'CONTACT.SOCIAL.DESC'})}
                  </p>
                  <div className='social-links'>
                    <a href='#' className='social-link telegram'>
                      <KTIcon iconName='message-text-2' className='social-icon' />
                      <span>{intl.formatMessage({id: 'CONTACT.SOCIAL.TELEGRAM'})}</span>
                    </a>
                    <a href='#' className='social-link instagram'>
                      <KTIcon iconName='instagram' className='social-icon' />
                      <span>{intl.formatMessage({id: 'CONTACT.SOCIAL.INSTAGRAM'})}</span>
                    </a>
                    <a href='#' className='social-link twitter'>
                      <KTIcon iconName='twitter' className='social-icon' />
                      <span>{intl.formatMessage({id: 'CONTACT.SOCIAL.TWITTER'})}</span>
                    </a>
                  </div>
                </div>
              </div>
              <div className='col-lg-6 col-12'>
                <img
                  src={toAbsoluteUrl('/media/illustrations/energy/1.webp')}
                  alt='شبکه‌های اجتماعی'
                  className='social-image'
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export {ContactUs}
